import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { matchesListSearch } from '../lib/listSearch'
import { cn, pickerFieldClass, pickerListClass, pickerOptionClass, pickerPanelClass } from '../lib/ui'

export type SearchableOption = {
  value: string
  primary: string
  secondary?: string
}

type PanelCoords = { top: number; left: number; width: number; maxHeight: number }

type SearchableSelectProps = {
  options: SearchableOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  ariaLabel?: string
  /** Связка с `<label htmlFor>` */
  inputId?: string
  clearable?: boolean
}

function fieldClass(open: boolean, disabled: boolean) {
  return pickerFieldClass(open, cn(disabled && 'cursor-not-allowed opacity-60'))
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Поиск…',
  emptyText = 'Ничего не найдено',
  disabled = false,
  ariaLabel,
  inputId,
  clearable = true,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [panelCoords, setPanelCoords] = useState<PanelCoords | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])
  const listId = useId()

  const selected = useMemo(
    () => (value ? options.find((o) => o.value === value) : undefined),
    [options, value],
  )

  const filtered = useMemo(
    () =>
      options.filter((o) =>
        matchesListSearch(query, o.primary, o.secondary),
      ),
    [options, query],
  )

  useEffect(() => {
    if (document.activeElement === inputRef.current) return
    setQuery(selected ? selected.primary : '')
  }, [selected?.value, selected?.primary])

  const updatePanelCoords = useCallback(() => {
    const el = inputRef.current
    if (!open || !el) return
    const rect = el.getBoundingClientRect()
    const pad = 8
    const minW = 260
    const width = Math.min(Math.max(rect.width, minW), window.innerWidth - pad * 2)
    let left = rect.left
    if (left + width > window.innerWidth - pad) left = window.innerWidth - width - pad
    if (left < pad) left = pad
    const top = rect.bottom + 4
    const capPx = Math.min(window.innerHeight * 0.7, 22 * 16)
    const maxHeight = Math.max(140, Math.min(capPx, window.innerHeight - top - pad))
    setPanelCoords({ top, left, width, maxHeight })
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      setPanelCoords(null)
      return
    }
    updatePanelCoords()
    window.addEventListener('resize', updatePanelCoords)
    window.addEventListener('scroll', updatePanelCoords, true)
    return () => {
      window.removeEventListener('resize', updatePanelCoords)
      window.removeEventListener('scroll', updatePanelCoords, true)
    }
  }, [open, updatePanelCoords])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function pick(v: string) {
    const name = options.find((o) => o.value === v)?.primary ?? ''
    valueRef.current = v
    onChange(v)
    setQuery(name)
    setOpen(false)
    inputRef.current?.blur()
  }

  function clearSelection() {
    onChange('')
    setQuery('')
    setOpen(true)
    inputRef.current?.focus()
  }

  function handleInputBlur() {
    requestAnimationFrame(() => {
      const a = document.activeElement
      if (panelRef.current?.contains(a) || wrapRef.current?.contains(a)) return
      setOpen(false)
      const v = valueRef.current
      const s = v ? options.find((o) => o.value === v) : undefined
      setQuery(s ? s.primary : '')
    })
  }

  const panel =
    open &&
    !disabled &&
    panelCoords &&
    createPortal(
      <div
        ref={panelRef}
        id={listId}
        className={pickerPanelClass}
        role="listbox"
        aria-label={ariaLabel || 'Варианты'}
        style={{
          top: panelCoords.top,
          left: panelCoords.left,
          width: panelCoords.width,
          maxHeight: panelCoords.maxHeight,
        }}
      >
        <ul className={pickerListClass}>
          {filtered.slice(0, 250).map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={pickerOptionClass(o.value === value)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o.value)}
              >
                <span className="text-[0.9rem] font-medium leading-snug">{o.primary}</span>
                {o.secondary ? <span className="text-xs text-slate-500">{o.secondary}</span> : null}
              </button>
            </li>
          ))}
        </ul>
        {filtered.length === 0 ? (
          <p className="m-0 px-4 py-3 text-center text-[0.88rem] text-slate-400">{emptyText}</p>
        ) : null}
        {clearable && value ? (
          <div className="border-t border-slate-100 bg-slate-50 px-2 py-1.5">
            <button
              type="button"
              className="cursor-pointer rounded-md border-none bg-transparent px-2 py-1 text-[0.82rem] text-slate-500 hover:bg-red-50 hover:text-red-700"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearSelection}
            >
              Сбросить выбор
            </button>
          </div>
        ) : null}
      </div>,
      document.body,
    )

  const fieldOpen = open && !disabled

  return (
    <div className="relative w-full" ref={wrapRef}>
      <div className={fieldClass(fieldOpen, disabled)}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className="min-w-0 flex-1 rounded-[10px] border-none bg-transparent px-2.5 py-2 text-[0.92rem] text-su-text-strong outline-none disabled:cursor-not-allowed"
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation()
              setOpen(false)
              const v = valueRef.current
              const s = v ? options.find((o) => o.value === v) : undefined
              setQuery(s ? s.primary : '')
              inputRef.current?.blur()
            }
            if (e.key === 'Enter' && open && filtered.length > 0) {
              e.preventDefault()
              pick(filtered[0].value)
            }
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-r-lg border-none bg-transparent px-2 hover:bg-su-surface-muted disabled:cursor-not-allowed disabled:hover:bg-transparent"
          aria-hidden
          title="Список"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (disabled) return
            setOpen((o) => !o)
            inputRef.current?.focus()
          }}
        >
          <span
            className={cn(
              'block h-0 w-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-slate-500 transition-transform',
              fieldOpen && 'rotate-180',
            )}
          />
        </button>
      </div>
      {panel}
    </div>
  )
}
